import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, ChevronDown, ChevronRight, Sparkles, Save, CheckCircle2, FileDown, Paperclip, BrainCircuit, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { LivrableEditor } from './livrableEditors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUT_LABELS = { a_faire: 'À faire', en_cours: 'En cours', soumis: 'Soumis par le porteur', valide: 'Validé' };
const STATUT_STYLES = { a_faire: 'bg-stone-100 text-stone-600 border-stone-200', en_cours: 'bg-amber-50 text-amber-700 border-amber-200', soumis: 'bg-violet-50 text-violet-700 border-violet-200', valide: 'bg-emerald-50 text-emerald-700 border-emerald-200' };

function LivrableCard({ liv, onSaved, hdrs }) {
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState(liv.content);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [analysing, setAnalysing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = React.useRef(null);

    const change = (c) => { setContent(c); setDirty(true); };

    const save = async (statut) => {
        setSaving(true);
        try {
            await axios.patch(`${API}/adrns/livrables/${liv.id}`, { content, ...(statut ? { statut } : {}) }, hdrs());
            toast.success(statut === 'valide' ? 'Livrable validé' : 'Livrable enregistré');
            setDirty(false);
            onSaved({ ...liv, content, statut: statut || (liv.statut === 'a_faire' ? 'en_cours' : liv.statut) });
            if (!statut && liv.statut === 'a_faire') {
                await axios.patch(`${API}/adrns/livrables/${liv.id}`, { statut: 'en_cours' }, hdrs());
            }
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSaving(false);
    };

    const aiDraft = async () => {
        setAiLoading(true);
        try {
            const r = await axios.post(`${API}/adrns/livrables/${liv.id}/ai`, {}, hdrs());
            setContent(r.data.draft);
            setDirty(true);
            toast.success('Premier jet généré par l\'IA — relisez puis enregistrez');
        } catch (e) { toast.error(e.response?.data?.detail || 'IA indisponible'); }
        setAiLoading(false);
    };

    const togglePorteurEdit = async (v) => {
        try {
            await axios.patch(`${API}/adrns/livrables/${liv.id}`, { porteur_can_edit: v }, hdrs());
            toast.success(v ? 'Formulaire ouvert au porteur' : 'Formulaire fermé au porteur');
            onSaved({ ...liv, porteur_can_edit: v });
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const uploadDoc = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const r = await axios.post(`${API}/adrns/livrables/${liv.id}/documents`, fd, hdrs());
            toast.success(r.data.deduplicated ? 'Document déjà joint' : 'Document joint au livrable');
            onSaved({ ...liv, documents: [...(liv.documents || []), r.data.document] });
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setUploading(false);
    };

    const analyse = async () => {
        setAnalysing(true);
        try {
            const r = await axios.post(`${API}/adrns/livrables/${liv.id}/analyse`, {}, hdrs());
            toast.success('Analyse Intelligence 360° générée');
            onSaved({ ...liv, analyse_ia: r.data.analyse });
        } catch (e) { toast.error(e.response?.data?.detail || 'Analyse indisponible'); }
        setAnalysing(false);
    };

    return (
        <div className="card-soft overflow-hidden" data-testid={`adrns-livrable-${liv.key}`}>
            <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-surface-muted/50 transition-colors" data-testid={`adrns-livrable-toggle-${liv.key}`}>
                {open ? <ChevronDown size={16} className="text-muted-foreground shrink-0" /> : <ChevronRight size={16} className="text-muted-foreground shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="font-medium">{liv.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{liv.hint}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium shrink-0 ${STATUT_STYLES[liv.statut]}`} data-testid={`adrns-livrable-statut-${liv.key}`}>
                    {STATUT_LABELS[liv.statut]}
                </span>
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-border pt-4">
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-muted/40 px-3 py-2">
                        <Switch checked={!!liv.porteur_can_edit} onCheckedChange={togglePorteurEdit} data-testid={`adrns-porteur-edit-${liv.key}`} />
                        <span className="text-sm">À remplir par le porteur</span>
                        <span className="text-xs text-muted-foreground">— le formulaire apparaît dans son espace « Mon accompagnement »</span>
                    </div>
                    <LivrableEditor type={liv.type} value={content} onChange={change} hint={liv.hint} testPrefix={`adrns-ed-${liv.key}`} />

                    <div className="rounded-lg border border-border p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Documents joints ({(liv.documents || []).length})</span>
                            <input type="file" ref={fileRef} className="hidden" onChange={e => { uploadDoc(e.target.files[0]); e.target.value = ''; }} data-testid={`adrns-liv-doc-input-${liv.key}`} />
                            <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`adrns-liv-doc-btn-${liv.key}`}>
                                {uploading ? <Loader2 size={13} className="animate-spin" /> : <><Paperclip size={13} className="mr-1" /> Joindre (PDF, Word, Excel, PPT)</>}
                            </Button>
                        </div>
                        {(liv.documents || []).map(doc => (
                            <div key={doc.id} className="flex items-center gap-2 text-sm" data-testid={`adrns-liv-doc-${doc.id}`}>
                                <FileText size={13} className="text-primary shrink-0" />
                                <span className="flex-1 truncate">{doc.name}</span>
                                <span className="text-xs text-muted-foreground">{(doc.size / 1024).toFixed(0)} Ko</span>
                            </div>
                        ))}
                    </div>

                    {liv.analyse_ia && (
                        <div className="rounded-lg border border-primary/25 bg-primary/5 p-4" data-testid={`adrns-analyse-${liv.key}`}>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary font-semibold mb-2">
                                <BrainCircuit size={14} /> Analyse Intelligence 360°
                                <span className="text-muted-foreground normal-case font-normal tracking-normal">— {new Date(liv.analyse_ia.at).toLocaleString('fr-FR')}{liv.analyse_ia.docs_analyses?.length ? ` · docs : ${liv.analyse_ia.docs_analyses.join(', ')}` : ''}</span>
                            </div>
                            <div className="text-sm leading-relaxed space-y-1">
                                {liv.analyse_ia.text.split('\n').map((l, i) => {
                                    const t = l.trim();
                                    if (!t) return <div key={i} className="h-1.5" />;
                                    if (t.startsWith('#')) return <div key={i} className="font-medium text-primary mt-2">{t.replace(/^#+\s*/, '')}</div>;
                                    if (t.startsWith('- ') || t.startsWith('• ')) return <div key={i} className="pl-4 relative"><span className="absolute left-0 text-primary">•</span>{t.slice(2)}</div>;
                                    return <p key={i}>{t.replace(/\*\*/g, '')}</p>;
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={aiDraft} disabled={aiLoading} data-testid={`adrns-ai-${liv.key}`}>
                            {aiLoading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Sparkles size={13} className="mr-1.5 text-primary" />}
                            {aiLoading ? 'Génération…' : 'Générer avec l\'IA'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={analyse} disabled={analysing} data-testid={`adrns-analyse-btn-${liv.key}`}>
                            {analysing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <BrainCircuit size={13} className="mr-1.5 text-primary" />}
                            {analysing ? 'Analyse…' : 'Analyser avec l\'IA'}
                        </Button>
                        <div className="flex-1" />
                        <Button size="sm" variant="outline" onClick={() => save()} disabled={saving || !dirty} data-testid={`adrns-save-${liv.key}`}>
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} className="mr-1.5" /> Enregistrer</>}
                        </Button>
                        <Button size="sm" onClick={() => save('valide')} disabled={saving || liv.statut === 'valide'} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`adrns-validate-${liv.key}`}>
                            <CheckCircle2 size={13} className="mr-1.5" /> Valider
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdrnsModulePage() {
    const { id, mid } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    const [data, setData] = useState(null);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    useEffect(() => {
        if (!session) return;
        axios.get(`${API}/adrns/dossiers/${id}/modules/${mid}`, hdrs())
            .then(r => setData(r.data))
            .catch(e => { toast.error(e.response?.data?.detail || e.message); navigate(`/app/adrns/dossiers/${id}`); });
        // eslint-disable-next-line
    }, [session, id, mid]);

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;
    const { module, livrables, dossier } = data;
    const valides = livrables.filter(l => l.statut === 'valide').length;

    const exportPdf = async () => {
        try {
            const r = await axios.get(`${API}/adrns/dossiers/${id}/evaluation.pdf`, { ...hdrs(), responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = `evaluation_${dossier.porteur_name}.pdf`; a.click();
            URL.revokeObjectURL(url);
            toast.success('Rapport PDF exporté');
        } catch { toast.error('Export impossible — renseignez d\'abord la grille d\'évaluation'); }
    };

    const onSaved = (updated) => setData(d => ({ ...d, livrables: d.livrables.map(l => l.id === updated.id ? updated : l) }));

    return (
        <div className="space-y-6" data-testid="adrns-module-page">
            <button onClick={() => navigate(`/app/adrns/dossiers/${id}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="adrns-module-back">
                <ArrowLeft size={14} /> Dossier {dossier.porteur_name}
            </button>
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow mb-1">Module {module.id} · {dossier.porteur_name}</div>
                    <h1 className="font-heading text-4xl">{module.nom}</h1>
                    <p className="text-sm text-foreground/60 mt-2">{module.description}</p>
                </div>
                {module.id === 5 && (
                    <Button variant="outline" onClick={exportPdf} data-testid="adrns-export-evaluation-pdf">
                        <FileDown size={15} className="mr-1.5 text-primary" /> Exporter le rapport PDF
                    </Button>
                )}
            </div>
            <div className="card-soft p-4 flex items-center gap-4">
                <Progress value={(valides / livrables.length) * 100} className="flex-1 h-2" />
                <div className="text-sm font-medium whitespace-nowrap" data-testid="adrns-module-progress">{valides}/{livrables.length} livrables validés</div>
            </div>
            <div className="space-y-3">
                {livrables.map(liv => <LivrableCard key={liv.id} liv={liv} onSaved={onSaved} hdrs={hdrs} />)}
            </div>
        </div>
    );
}
